import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/assignment-stages` (v0.4 章⑥ F-6、V-5 + 追加7要件⑧ D-238/D-253)。
 * 下書き自動生成が `sort_order` 順に実行するステージのパイプラインを定義する。
 * `priority_score` は v0.4 では器のみ (no-op、D-19) — 履歴が無いため動作しない。選択肢からは隠さず
 * 注記を添える (D-19 の「因子の記録は Phase A から行う」方針と揃える)。
 * ⚠️ D-238: ステージ構成は node 軸の階層継承 (D-59 串刺し) に乗る。「テナント全体 (既定)」は
 * ノード木の1つ上、node_id=null に相当する仮想選択肢 (StaffingSettingsPage の NodeSelector とは
 * 異なり、この画面の唯一の起点だった既定集合を引き続き直接編集できるようにするための追加)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §6-1 / §6-3
 *       docs/design/v04-shift-additional-requirements.md §7-3(a) / §10 D-238
 *
 * ⚠️ 並び替え: assignment_stages.sort_order には UNIQUE 制約が無い (migrations/0054_*.sql、
 * PRIMARY KEY は (stage_id, effective_from)、部分UNIQUEは (tenant_id, stage_id) WHERE
 * effective_to IS NULL のみ)。visibility_rules/standard_rate_tiers (D-210、部分UNIQUE制約あり)
 * とは異なり、ConditionSetEditor.tsx の move() と同じ2件並列PUTで安全 (一時退避値を経由する
 * 3段階の逐次PUTは不要)。並び替えは同一ノードの集合内で完結する (node_id は改定不可)。
 */

const ASSIGNMENT_STAGE_USAGE_POINT_ID = 'assignment_stage_subject';
const TENANT_DEFAULT = '__tenant_default__';

type Stage = {
  stage_id: string; node_id: string | null; sort_order: number; label: string; condition_set_id: string | null;
  strategy: 'expand_standing' | 'preference_based' | 'priority_score'; is_enabled: boolean; effective_from: string;
};
type SourceInfo = { node_id: string; source_node_id: string | null; source_kind: InheritanceSourceKind };

const STRATEGY_LABELS: Record<Stage['strategy'], string> = {
  expand_standing: '恒常パターン展開',
  preference_based: '希望に基づく割当 (貪欲法)',
  priority_score: '優先度スコアリング',
};

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

function NodeSelector({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象ノード</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={TENANT_DEFAULT}>テナント全体 (既定)</SelectItem>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type StageDraft = {
  stage_id: string | null; label: string; strategy: Stage['strategy']; condition_set_id: string | null; is_enabled: boolean;
};
const emptyStageDraft = (): StageDraft => ({ stage_id: null, label: '', strategy: 'expand_standing', condition_set_id: null, is_enabled: true });

const buildFullPayload = (s: Stage, patch: Partial<{ sort_order: number }> = {}) => ({
  label: s.label,
  strategy: s.strategy,
  condition_set_id: s.condition_set_id,
  is_enabled: s.is_enabled,
  sort_order: patch.sort_order ?? s.sort_order,
});

function StageModal({ draft, setDraft, conditionSets, nextSortOrder, targetNodeId, onClose, onSaved }: {
  draft: StageDraft; setDraft: (d: StageDraft) => void; conditionSets: { set_id: string; label: string | null }[];
  nextSortOrder: number; targetNodeId: string | null; onClose: () => void; onSaved: () => void;
}) {
  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        label: draft.label.trim(),
        strategy: draft.strategy,
        condition_set_id: draft.condition_set_id,
        is_enabled: draft.is_enabled,
        // ⚠️ node_id は改定 (PUT) では受け付けない (サーバ側で作成時の値に固定、D-238)。新規作成のみ渡す。
        ...(draft.stage_id ? { sort_order: undefined } : { sort_order: nextSortOrder, node_id: targetNodeId }),
      };
      return draft.stage_id
        ? apiFetch(`/settings/assignment-stages/${draft.stage_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/assignment-stages', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const canSave = draft.label.trim().length > 0;

  return (
    <Modal open title={draft.stage_id ? 'ステージを編集' : 'ステージを追加'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">ラベル</label>
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 恒常パターンの展開" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">戦略</label>
          <Select value={draft.strategy} onValueChange={(v) => setDraft({ ...draft, strategy: v as Stage['strategy'] })}>
            <SelectTrigger aria-label="戦略"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.entries(STRATEGY_LABELS) as [Stage['strategy'], string][]).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {draft.strategy === 'priority_score' && (
            <p className="text-xs text-alert-text mt-1 flex items-start gap-1">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
              現時点ではこの戦略は動作しません (v0.4は設定項目のみ実装です。優先度の因子データが蓄積してから動作するようになります)。
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">対象者 (任意)</label>
          <Select value={draft.condition_set_id ?? '__all__'} onValueChange={(v) => setDraft({ ...draft, condition_set_id: v === '__all__' ? null : v })}>
            <SelectTrigger aria-label="対象者"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">対象を絞らない (全員)</SelectItem>
              {conditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
            </SelectContent>
          </Select>
          {conditionSets.length === 0 && (
            <p className="text-xs text-text-sub mt-1">条件セットがまだありません。この画面下部の「対象者条件を管理」から先に作成してください。</p>
          )}
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">状態</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="状態">
            {([true, false] as const).map((v) => (
              <button key={String(v)} type="button" aria-pressed={draft.is_enabled === v} onClick={() => setDraft({ ...draft, is_enabled: v })}
                className={`px-3 min-h-[44px] text-xs font-medium ${draft.is_enabled === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {v ? '有効' : '無効'}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function StagesList({ stages, canManage, isOwn, targetNodeId, conditionSets, onRequestOwn, onChanged }: {
  stages: Stage[]; canManage: boolean; isOwn: boolean; targetNodeId: string | null;
  conditionSets: { set_id: string; label: string | null }[]; onRequestOwn: () => void; onChanged: () => void;
}) {
  const [draft, setDraft] = useState<StageDraft | null>(null);
  const [reordering, setReordering] = useState(false);
  const setLabel = (id: string) => conditionSets.find((s) => s.set_id === id)?.label ?? id;

  const sorted = useMemo(() => [...stages].sort((a, b) => a.sort_order - b.sort_order || a.stage_id.localeCompare(b.stage_id)), [stages]);
  const nextSortOrder = sorted.length > 0 ? Math.max(...sorted.map((s) => s.sort_order)) + 1 : 0;
  const canEdit = canManage && isOwn;

  const move = async (stage: Stage, direction: -1 | 1) => {
    const idx = sorted.findIndex((s) => s.stage_id === stage.stage_id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const target = sorted[targetIdx]!;
    setReordering(true);
    try {
      await Promise.all([
        apiFetch(`/settings/assignment-stages/${stage.stage_id}`, { method: 'PUT', body: JSON.stringify(buildFullPayload(stage, { sort_order: target.sort_order })) }),
        apiFetch(`/settings/assignment-stages/${target.stage_id}`, { method: 'PUT', body: JSON.stringify(buildFullPayload(target, { sort_order: stage.sort_order })) }),
      ]);
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '並び替えに失敗しました');
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-2">
      {!isOwn && canManage && (
        <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3">
          <p className="text-xs text-alert-text">上位ノードの設定を継承しています。この店舗専用の設定を追加すると、以降はこの店舗独自の定義になります。</p>
          <Button variant="secondary" size="sm" onClick={onRequestOwn}>この店舗の設定を追加する</Button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-text-sub">上から順に実行されます。ステージが1件もない場合、下書き生成ボタンを押しても何も生成されません。</p>
        {canEdit && <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyStageDraft())}>ステージを追加</Button>}
      </div>
      {sorted.length === 0 ? (
        <EmptyState message="ステージがまだありません" description="「ステージを追加」から下書き生成のパイプラインを組み立ててください。" />
      ) : (
        <ul className="space-y-2">
          {sorted.map((s, i) => (
            <li key={s.stage_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0 flex items-center gap-2">
                {canEdit && (
                  <div className="flex flex-col shrink-0">
                    <button type="button" aria-label="上へ" disabled={i === 0 || reordering} onClick={() => move(s, -1)}
                      className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button type="button" aria-label="下へ" disabled={i === sorted.length - 1 || reordering} onClick={() => move(s, 1)}
                      className="w-11 h-11 sm:w-8 sm:h-6 inline-flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-30"><ChevronDown size={14} /></button>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-body break-words">
                    {s.label}
                    {!s.is_enabled && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border align-middle">無効</span>}
                  </p>
                  <p className="text-xs text-text-sub mt-0.5">
                    {STRATEGY_LABELS[s.strategy]}
                    {s.strategy === 'priority_score' && <span className="text-alert-text"> (現時点では動作しません)</span>}
                    {' ・ '}{s.condition_set_id ? setLabel(s.condition_set_id) : '全員'}
                  </p>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setDraft({
                    stage_id: s.stage_id, label: s.label, strategy: s.strategy, condition_set_id: s.condition_set_id, is_enabled: s.is_enabled,
                  })}>編集</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {draft && (
        <StageModal draft={draft} setDraft={setDraft} conditionSets={conditionSets} nextSortOrder={nextSortOrder} targetNodeId={targetNodeId}
          onClose={() => setDraft(null)} onSaved={() => { onChanged(); setDraft(null); }} />
      )}
    </div>
  );
}

export function AssignmentStagesSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasPermission(user?.effective_permissions, 'shift_periods', 'manage');
  const qc = useQueryClient();
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string>(TENANT_DEFAULT);
  const [forceOwn, setForceOwn] = useState(false);
  const targetNodeId = selectedNodeId === TENANT_DEFAULT ? null : selectedNodeId;

  // ⚠️ node_id=null (テナント既定) はノード木の上に位置し祖先ウォークの対象にならない
  // (自分自身が唯一の候補、resolveAssignmentStageSourceNode は実ノードのみ受け付ける)。
  // 常に isOwn=true として扱い /source は呼ばない。
  const { data: source } = useQuery<SourceInfo>({
    queryKey: ['assignment-stage-source', targetNodeId],
    queryFn: () => apiFetch(`/settings/assignment-stages/source?node_id=${encodeURIComponent(targetNodeId!)}`),
    enabled: targetNodeId !== null,
  });
  const isOwnActual = targetNodeId === null ? true : source?.source_kind === 'self';
  const isOwn = isOwnActual || forceOwn;
  const effectiveNodeId = targetNodeId === null ? null : (isOwn ? targetNodeId : (source?.source_node_id ?? null));

  const { data, isLoading } = useQuery<{ node_id: string | null; stages: Stage[] }>({
    queryKey: ['assignment-stages', effectiveNodeId],
    queryFn: () => apiFetch<{ node_id: string | null; stages: Stage[] }>(`/settings/assignment-stages${effectiveNodeId ? `?node_id=${encodeURIComponent(effectiveNodeId)}` : ''}`),
    enabled: targetNodeId === null || isOwnActual || !!source,
  });
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const nodeNameById = useMemo(() => new Map(flattenNodes(nodesData?.roots ?? []).map(({ node }) => [node.node_id, node.name])), [nodesData]);
  const { data: catalog } = useQuery<ConditionCatalog>({ queryKey: ['condition-catalog'], queryFn: () => apiFetch('/condition-catalog'), staleTime: 5 * 60 * 1000 });
  const { data: setsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', ASSIGNMENT_STAGE_USAGE_POINT_ID],
    queryFn: () => apiFetch(`/condition-sets?usage_point_id=${ASSIGNMENT_STAGE_USAGE_POINT_ID}`),
  });
  const usagePoint = catalog?.usage_points.find((u) => u.usage_point_id === ASSIGNMENT_STAGE_USAGE_POINT_ID);
  const attributeTypesById = useMemo(() => new Map((catalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t])), [catalog]);
  const conditionSets = useMemo(() => (setsData?.sets ?? []).map((s) => ({ set_id: s.set_id, label: s.label })), [setsData]);

  const onChanged = () => {
    // ⚠️ StaffingSettingsPage と同じ理由で forceOwn はここではリセットしない (再フェッチ完了前の
    // 一瞬 isOwnActual が古いままで継承バナーがチラつくのを避ける)。ノード切替時のみリセットする。
    qc.invalidateQueries({ queryKey: ['assignment-stage-source', targetNodeId] });
    qc.invalidateQueries({ queryKey: ['assignment-stages'] });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">下書き生成ステージ</h2>
        <p className="text-sm text-text-sub mt-1">
          シフトの下書き自動生成が実行する処理の順序を定義します。「恒常パターン展開」→「希望に基づく割当」の順が標準的な構成です。
          対象者を絞ることで、店舗・雇用形態ごとに異なる扱いをステージ単位で表現できます。ノードごとに構成を変えることもでき、
          上書きしていないノードは上位ノード・テナント全体の既定を継承します。
        </p>
        {permsReady && !canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (編集には対象ノードの管理権限が必要です)。</p>}
      </div>

      <NodeSelector value={selectedNodeId} onChange={(id) => { setSelectedNodeId(id); setForceOwn(false); }} />

      {targetNodeId !== null && !source ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-3">
          {targetNodeId !== null && !forceOwn && (
            <InheritanceSourceBadge
              sourceKind={source!.source_kind}
              sourceNodeLabel={source!.source_node_id ? (nodeNameById.get(source!.source_node_id) ?? source!.source_node_id) : null}
            />
          )}
          <StagesList stages={data?.stages ?? []} canManage={canManage} isOwn={isOwn} targetNodeId={targetNodeId}
            conditionSets={conditionSets} onRequestOwn={() => setForceOwn(true)} onChanged={onChanged} />
        </div>
      )}

      {usagePoint && (
        <div className="border border-surface-border rounded-lg bg-white">
          <button type="button" onClick={() => setShowConditionEditor((v) => !v)} aria-expanded={showConditionEditor}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm font-semibold text-text-body">
            対象者条件を管理
          </button>
          {showConditionEditor && (
            <div className="px-3 pb-4">
              <ConditionSetEditor usagePointId={ASSIGNMENT_STAGE_USAGE_POINT_ID} usagePoint={usagePoint} attributeTypesById={attributeTypesById} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
